import { Box } from '@mantine/core';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AxiosError, isAxiosError } from 'axios';
import { useRef } from 'react';
import { z } from 'zod';

import { ChangePassword } from '@/module/Security/ChangePassword';
import { ChangePasswordSchema } from '@/module/Security/model/ChangePasswordSchema';
import { getCurrentUserOptions, useChangePassword, useLogout } from '@/module/Security/model/SecurityQuery';

import { useAppNotification } from '@feature/utils';

const fallback = '/' as const;

export const Route = createFileRoute('/change-password')({
  component: ChangePasswordScreen,
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  beforeLoad: async ({ context: { queryClient }, search, location, preload }) => {
    let passwordChangeRequired: boolean;
    try {
      ({ passwordChangeRequired } = await queryClient.ensureQueryData(getCurrentUserOptions()));
    } catch (err: unknown) {
      if (isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
        throw redirect({ to: '/login', search: { redirect: location.href } });
      }
      throw err;
    }

    if (!passwordChangeRequired && !preload) {
      throw redirect({ to: search.redirect || fallback, replace: true });
    }
  },
});

function ChangePasswordScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const notification = useAppNotification();

  const { mutateAsync, isPending } = useChangePassword();
  const logout = useLogout();

  // A second submit in the same frame would send the dead temporary password and fail after a success.
  const inFlight = useRef(false);

  const onSubmit = async ({ currentPassword, newPassword }: ChangePasswordSchema) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    try {
      await mutateAsync({ currentPassword, newPassword });
    } catch (err: unknown) {
      inFlight.current = false;
      notification.error(err as AxiosError);
      return;
    }

    notification.success();
    await navigate({ to: search.redirect || fallback, replace: true });
  };

  // The reload to /login empties the client state even when the server logout fails.
  const onLogout = () =>
    logout
      .mutateAsync()
      .catch(() => undefined)
      .finally(() => window.location.assign('/login'));

  return (
    <Box mt="3%">
      <ChangePassword
        onSubmit={onSubmit}
        onLogout={onLogout}
        isSubmitting={isPending || logout.isPending}
      />
    </Box>
  );
}
